import { useState, useEffect, useCallback } from 'react';
import { logmarData } from '../../../helpers/data';
import { useTestContext } from '../../../context/testContext';
import { useCalibration } from '../../../context/calibrationContext';
import { usePostMessage, useReceiveMessage } from '../../../context/messageContext';
import { calibrationConstants } from '../../../helpers/calibration-constants';
import { getLogmarLineSize500mm } from '../../../helpers/functions';
import { useLogmarData } from '../../../helpers/reducers';
import { Button, SessionActions, useLayoutMode } from '../../../ui';
import { ExamBody, ScoringGrid, Column, Question, Divider, Row, CountButtons, useConfirmNavigation } from '../exams/common';
import { LogmarChart } from '../exams/LogmarChart';
import AutoResizeControls from '../exams/AutoResizeControls';

const LogmarTest = ({ eyeTested, logmarType, updateStep }) => {
  const {
    calibrationData: { distanceFromScreen, pixelDensity, methodSelected },
    updateCalibrationData,
  } = useCalibration();
  const { testData, logmarCharacters, generateLogmarCharacters, updateTestData } = useTestContext();
  const [charsSeen, setCharsSeen] = useState();
  const [currentLogmar, setCurrentLogmar] = useState(testData ? testData[eyeTested] : null);
  const messageFromPatient = useReceiveMessage();
  const postMessage = usePostMessage();
  const [initialLoad, setInitialLoad] = useState(false);
  const { pixelDensityReference, patientDistanceReference } = calibrationConstants;
  const { currentLogmarData, updateLogmarData } = useLogmarData();
  const mode = useLayoutMode();
  const confirmNavigation = useConfirmNavigation();
  const [highlightedCharacter, setHighlightedCharacter] = useState('');

  // Pointing at a letter of the current line (was in LogmarTableDuringTests).
  const handleCharacterHighlight = useCallback(
    (charId) => {
      postMessage({ type: 'highlightLogmarCharacter', content: charId });
      return setHighlightedCharacter(charId);
    },
    [postMessage],
  );

  useEffect(() => {
    handleCharacterHighlight(null);
  }, [currentLogmar, handleCharacterHighlight]);

  useEffect(() => {
    updateTestData({ testInProgress: true });
  }, [updateTestData]);

  const endTest = useCallback(() => {
    updateTestData({ [eyeTested]: { score: currentLogmarData.finalScore } });
    const nextStep = eyeTested === 'rightEye' && testData.testLeftEye ? 'leftEyeSetup' : 'results';
    if (nextStep === 'results') postMessage({ type: 'endOfTest' });
    return updateStep(nextStep);
  }, [currentLogmarData.finalScore, eyeTested, updateTestData, testData.testLeftEye, updateStep, postMessage]);

  const selectPreviousLogmar = () => {
    generateLogmarCharacters();
    return updateLogmarData({ type: 'incrementLogmarSize', payload: currentLogmar });
  };

  const selectNextLogmar = useCallback(() => {
    setCharsSeen(undefined);
    if (currentLogmar.id === 13) return endTest();
    return updateLogmarData({ type: 'decrementLogmarSize' });
  }, [currentLogmar.id, endTest, updateLogmarData]);

  const getLogmarFontSizeRefactor = useCallback(
    (logmar, distance) => {
      let lineSize;
      if (patientDistanceReference === 500) {
        lineSize = getLogmarLineSize500mm(logmar.id);
      }
      const fontSize = (lineSize * distance) / patientDistanceReference / pixelDensity;
      const fontSizeAdjustment = (pixelDensity * testData.fontAdjustmentPercentage) / pixelDensityReference / 100;
      const adjustedFontSize = fontSize + fontSize * fontSizeAdjustment;

      return adjustedFontSize;
    },
    [patientDistanceReference, pixelDensity, pixelDensityReference, testData.fontAdjustmentPercentage],
  );

  const updateLogmarFontSizeRefactor = useCallback(
    (logmar, distance) => {
      const adjustedFontSize = getLogmarFontSizeRefactor(logmar, distance);
      postMessage({
        type: 'resizeLogmar',
        content: {
          fontSize: Math.round(adjustedFontSize),
          distanceReferenceInMM: distance,
          logmarCharacters: logmarCharacters[logmar.id],
        },
      });
    },
    [getLogmarFontSizeRefactor, logmarCharacters, postMessage],
  );

  useEffect(() => {
    updateLogmarFontSizeRefactor(currentLogmarData.logmarSize, distanceFromScreen);
  }, [currentLogmarData.logmarSize, distanceFromScreen, updateLogmarFontSizeRefactor]);

  useEffect(() => {
    setCurrentLogmar(currentLogmarData.logmarSize);
  }, [currentLogmarData.logmarSize]);

  useEffect(() => {
    if (messageFromPatient.type === 'patientMovementDetected') {
      updateCalibrationData({ distanceFromScreen: messageFromPatient.content.patientDistance });

      if (messageFromPatient.content.patientIsMoving === false) {
        updateLogmarFontSizeRefactor(currentLogmar, messageFromPatient.content.patientDistance);
      }
    }
  }, [messageFromPatient, currentLogmar, updateCalibrationData, updateLogmarFontSizeRefactor]);

  useEffect(() => {
    if (initialLoad === false) {
      updateLogmarData({ type: 'setInitialLogmarSize', payload: testData[eyeTested] });
      postMessage({
        type: 'visualAcuityTest',
        content: {
          fontSize: Math.round(getLogmarFontSizeRefactor(currentLogmar, distanceFromScreen)),
          distanceFromScreen: distanceFromScreen,
          logmarCharacters: logmarCharacters[currentLogmar.id],
          logmarType: logmarType,
          methodSelected: methodSelected,
          testMode: methodSelected !== 'manual' ? 'auto' : testData.testMode,
        },
      });
      setInitialLoad(true);
    }
  }, [initialLoad]);

  useEffect(() => {
    if (charsSeen !== undefined) {
      const multiplier = testData.lettersToUse === 'sloan' ? 0.02 : 0.025;
      const amountToSubtractFromScore = charsSeen * multiplier;
      updateLogmarData({ type: 'adjustFinalScore', payload: amountToSubtractFromScore });
      selectNextLogmar();
    }
  }, [charsSeen, updateLogmarData, selectNextLogmar, testData.lettersToUse]);

  const selectPreviousDisabled = currentLogmar.id === 1 || currentLogmar.id === testData.maxPermittedLogmar.id;
  const numbers = testData.lettersToUse === 'glasgow' ? 4 : 5;

  return (
    <ExamBody>
      <ScoringGrid left={mode === 'landscape' ? '1.15fr' : '0.9fr'} right='1fr' fill>
        <Column fill style={{ minHeight: mode === 'phone' ? 200 : 0, maxHeight: mode === 'phone' ? 210 : 'none' }}>
          <LogmarChart
            data={logmarData}
            currentId={currentLogmar?.id}
            characters={logmarCharacters}
            highlighted={highlightedCharacter}
            onHighlight={handleCharacterHighlight}
            variant={mode === 'landscape' ? 'table' : 'list'}
          />
        </Column>
        <Column gap={16}>
          <Question>How many characters seen?</Question>
          <Row>
            <Button variant='destructive' size='lg' onClick={() => endTest()}>
              0 / End Test
            </Button>
          </Row>
          <CountButtons count={numbers} onPick={(num) => setCharsSeen(num)} />
          <Divider />
          <Row>
            <Button variant='secondary' size='lg' onClick={() => selectPreviousLogmar()} disabled={selectPreviousDisabled}>
              Previous logMAR
            </Button>
          </Row>
          <AutoResizeControls />
        </Column>
      </ScoringGrid>
      <SessionActions
        onBack={() =>
          confirmNavigation().then(() => {
            updateTestData({ [eyeTested]: null, testInProgress: false });
            updateStep(`${eyeTested}Setup`);
          })
        }
      />
    </ExamBody>
  );
};

export default LogmarTest;
